import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import AddIcon from "@mui/icons-material/Add";
import { Button, Stack } from "@mui/material";
import EmptyCardDataRooms from "assets/images/EmptyCardDataRooms.png";
import { AsyncContentMui } from "components";
import { ConnectionCard, ConnectionCardProps, ConnectionsStatuses, RoomsManageBar } from "components/data-rooms";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { RoomReconnect } from "components/modals";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { useFormatDateHook, useModals, useNavigate, useQueryHook } from "hooks";
import { useConnectionsSelect, useDataRoomsActions, useDateFormatSelect, useDealActions } from "store";
import { ConnectionParams, ConnectionPreview } from "store/types";

export const DataRooms: React.FC = () => {
  const connections = useConnectionsSelect();
  const formatDateHook = useFormatDateHook();
  const dateFormat = useDateFormatSelect();
  const formatDate = (date?: string | number | Date | null | undefined) => {
    return formatDateHook({ date, format: dateFormat });
  };

  const [connectId, setConnectId] = useState<number | null>(null);
  const [connectParams, setConnectParams] = useState<ConnectionParams | null>(null);
  const [nextConnection, setNextConnection] = useState<Function | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const {
    getConnectionsAction,
    getConnectionsStatusAction,
    deleteConnection,
    syncInvestorsToDeals,
    updateConnectionConfig
  } = useDataRoomsActions();
  const { getInvestorsAction } = useDealActions();
  const { openDataroomConnect } = useModals();
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    setIsActive(true);
    return () => {
      setIsActive(false);
    };
  }, []);

  const hasEffectRun = useRef(false);

  const { loading, refetch } = useQueryHook(getConnectionsAction);

  useEffect(() => {
    const checkConnectionStatus = async () => {
      if (connections.length > 0 && !hasEffectRun.current && connectId == null) {
        hasEffectRun.current = true;
        for (const connection of connections) {
          await getConnectionsStatusAction(connection);
          if (!isActive || !hasEffectRun.current) return;
        }
      }
    };
    checkConnectionStatus();
  }, [connections]);

  const onConnectClick = useCallback(
    (connection: ConnectionPreview) => {
      if (connection.last_login_error === "2FA_REQUIRED") {
        return setConnectId(connection.id);
      }
      hasEffectRun.current = false;
      navigate({ route: "dataRoom", params: { room_id: connection.id } });
    },
    [navigate]
  );

  const connectionsPreview = useMemo(() => {
    const mappedConnections = connections.map<ConnectionCardProps["item"]>((conn) => ({
      id: conn.id.toString(),
      status:
        conn.is_login_valid == null || connectId === conn.id
          ? "loading"
          : conn.last_login_error === "2FA_REQUIRED"
            ? "2FA error"
            : conn.is_login_valid
              ? "ok"
              : "error",
      username: conn.username,
      last_connected: conn.last_connected || undefined,
      title: conn.type_title,
      subtitle: conn.subdomain || null,
      last_index: conn.last_index || undefined,
      last_sync: conn.last_sync || undefined,
      mfa: conn.mfa || false,
      logo: conn.logo || undefined,
      enable_auto_sync: conn.enable_auto_sync
    }));

    // Filter connections based on search term
    if (!searchTerm.trim()) {
      return mappedConnections;
    }

    const searchLower = searchTerm.toLowerCase();
    return mappedConnections.filter((conn) => {
      // Search in all available fields from the original connection data
      const originalConn = connections.find((c) => c.id.toString() === conn.id);
      if (!originalConn) return false;

      return (
        // Basic fields
        conn.title.toLowerCase().includes(searchLower) ||
        conn.username.toLowerCase().includes(searchLower) ||
        (conn.subtitle && conn.subtitle.toLowerCase().includes(searchLower)) ||
        conn.status.toLowerCase().includes(searchLower) ||
        // Date fields
        (conn.last_connected && formatDate(conn.last_connected).toLowerCase().includes(searchLower)) ||
        (conn.last_index && formatDate(conn.last_index).toLowerCase().includes(searchLower)) ||
        (conn.last_sync && formatDate(conn.last_sync).toLowerCase().includes(searchLower)) ||
        (originalConn.last_download && formatDate(originalConn.last_download).toLowerCase().includes(searchLower)) ||
        (originalConn.last_login_error_timestamp &&
          formatDate(originalConn.last_login_error_timestamp).toLowerCase().includes(searchLower)) ||
        (originalConn.last_login_timestamp &&
          formatDate(originalConn.last_login_timestamp).toLowerCase().includes(searchLower)) ||
        // Additional fields from ConnectionPreview
        (originalConn.type && originalConn.type.toLowerCase().includes(searchLower)) ||
        (originalConn.updated_at && formatDate(originalConn.updated_at).toLowerCase().includes(searchLower)) ||
        (originalConn.last_download && formatDate(originalConn.last_download).toLowerCase().includes(searchLower)) ||
        (originalConn.last_login_error && originalConn.last_login_error.toLowerCase().includes(searchLower)) ||
        (originalConn.last_login_error_timestamp &&
          originalConn.last_login_error_timestamp.toString().toLowerCase().includes(searchLower)) ||
        (originalConn.last_login_timestamp &&
          originalConn.last_login_timestamp.toString().toLowerCase().includes(searchLower)) ||
        (originalConn.status && originalConn.status.toLowerCase().includes(searchLower)) ||
        // File counts
        (originalConn.files &&
          originalConn.files.indexed &&
          originalConn.files.indexed.toString().includes(searchLower)) ||
        (originalConn.files &&
          originalConn.files.downloaded &&
          originalConn.files.downloaded.toString().includes(searchLower)) ||
        // Boolean fields as text
        (originalConn.is_logged && "logged in".includes(searchLower)) ||
        (originalConn.is_login_valid && "valid login".includes(searchLower)) ||
        (originalConn.mfa && "mfa enabled".includes(searchLower))
      );
    });
  }, [connections, connectId, searchTerm]);

  const isShowAllFilesEnabled = !connections.reduce((acc, { files }) => {
    if (!files) return acc;
    return acc + files.indexed;
  }, 0);

  const handleConnectAll = useCallback(async () => {
    const next = () => {
      return new Promise<void>((resolve) => {
        setNextConnection(() => resolve);
      });
    };

    for (const room of connections) {
      if (!room.is_login_valid) {
        setConnectParams({ type: room.type, username: room.username });
        setConnectId(room.id);
        await next();
        setNextConnection(null);
      }
    }
  }, [connections]);

  const handleImportInvestors = useCallback(
    async ({ matchWithDeals, toastMsg }: { matchWithDeals: boolean; toastMsg: string }) => {
      const validConnection = connections.filter((x) => x.is_login_valid).map((x) => x.id);

      for (let index = 0; index < validConnection.length; index++) {
        const id = validConnection[index];
        try {
          await syncInvestorsToDeals(id, matchWithDeals);
        } catch (e) {
          console.log(e);
        }
      }

      getInvestorsAction();
      toast.success(toastMsg, {
        position: "top-center"
      });
    },
    [connections]
  );

  const connectionToRefetch = useMemo(() => {
    return connections.find((conn) => conn.id === connectId) || null;
  }, [connections, connectId]);

  return (
    <Stack sx={{ flexGrow: 1, overflow: "hidden", gap: "16px", position: "relative" }}>
      <AsyncContentMui spinnerProps={{ size: 150 }} isLoading={loading}>
        {!connectionsPreview.length ? (
          <EmptyCard
            title={EMPTY_CARD_CONTENT.dataRooms.title}
            description={{
              text: EMPTY_CARD_CONTENT.dataRooms.description,
              externalText: EMPTY_CARD_CONTENT.dataRooms.externalDescription
            }}
            image={{
              src: EmptyCardDataRooms,
              sx: { width: 315, height: 140 }
            }}
            actionsElement={
              <Button
                variant="contained"
                onClick={() => openDataroomConnect()}
                startIcon={<AddIcon />}
                sx={{ bgcolor: "#071F35" }}
              >
                Add Portal
              </Button>
            }
          />
        ) : (
          <>
            <RoomsManageBar
              isShowAllFilesEnabled={isShowAllFilesEnabled}
              refetchRooms={refetch}
              connectAll={handleConnectAll}
              importInvestors={handleImportInvestors}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />
            <Stack
              direction="row"
              sx={{
                flexWrap: "wrap",
                gap: "16px",
                overflow: "auto",
                pt: "1px",
                pb: "15px"
              }}
            >
              {connectionsPreview.map((item, index) => (
                <ConnectionCard
                  key={item.id}
                  item={item}
                  onClick={() => onConnectClick(connections[index])}
                  onConnect={async () => {
                    setConnectParams({ type: connections[index].type, username: item.username });
                    setConnectId(connections[index].id);
                  }}
                  onDelete={async () => {
                    await deleteConnection(connections[index].id);
                  }}
                  onToggleAutoSync={async () => {
                    const currentValue = item.enable_auto_sync ?? true;
                    await updateConnectionConfig(connections[index].id, {
                      enable_auto_sync: !currentValue
                    });
                    await getConnectionsAction();
                  }}
                />
              ))}
            </Stack>
          </>
        )}
      </AsyncContentMui>
      <RoomReconnect
        connectParams={connectParams}
        showSkip={nextConnection != null}
        onSkip={() => {
          setConnectId(null);
          if (nextConnection != null) {
            nextConnection();
          }
        }}
        onCancel={() => setConnectId(null)}
        onClose={() => {
          setConnectId(null);
          hasEffectRun.current = false;
          if (nextConnection != null) {
            nextConnection();
          }
        }}
        connectionId={connectId}
        refetchRoom={() => connectionToRefetch && getConnectionsStatusAction(connectionToRefetch)}
      />
      <ConnectionsStatuses />
    </Stack>
  );
};
